/**
 * The list a time combobox offers, and the reading of whatever gets typed at it.
 *
 * Pure string work, deliberately: a time of day is `HH:mm` on both sides of the wire, and
 * nothing here needs a date, a zone or a `Date` to decide that 09:30 comes after 09:15.
 *
 * The shape is the one the US Web Design System settles on for scheduling -- one list of
 * real times that typing *filters*, rather than an hour column and a minute column. Two
 * columns ask for two decisions; nobody thinks "nine, then thirty", they think "half nine".
 */

/** Minutes in a day, which is where the list stops. */
const MINUTES_IN_DAY = 24 * 60;

export const DEFAULT_TIME_STEP = 15;

const pad = (part: number): string => String(part).padStart(2, '0');

/** `540` as `09:00`. */
export const minutesToClock = (minutes: number): string =>
    `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** `09:00` as `540`, or null for anything that is not a clock time. */
export const clockToMinutes = (clock: string): number | null => {
    const match: RegExpMatchArray | null = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(clock);

    return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/**
 * Every time of day at `step` minutes, from `from` to `to` inclusive.
 *
 * Bounded rather than always the whole day, so the clinic's own opening hours can narrow it
 * where they are known -- but never as a *rule*: the therapist's diary passes no bounds at
 * all, because an emergency at eight in the evening is a real thing.
 */
export const timeOptions = (
    step: number = DEFAULT_TIME_STEP,
    from: string = '00:00',
    to: string = '23:59'
): string[] => {
    const start: number = clockToMinutes(from) ?? 0;
    const end: number = Math.min(clockToMinutes(to) ?? MINUTES_IN_DAY - 1, MINUTES_IN_DAY - 1);
    const safeStep: number = step > 0 ? step : DEFAULT_TIME_STEP;

    const options: string[] = [];

    for (let minutes = start; minutes <= end; minutes += safeStep) {
        options.push(minutesToClock(minutes));
    }

    return options;
};

/**
 * What somebody meant by what they typed.
 *
 * Read off the digits alone, so `9`, `9:30`, `930` and `0930` all land somewhere sensible
 * and a stray colon or space costs nothing. This is the half of the combobox that makes it
 * quicker than the list for anyone who already knows the time they want:
 *
 *   `9`    -> 09:00      a lone hour is the top of it
 *   `19`   -> 19:00
 *   `930`  -> 09:30      three digits is H:mm
 *   `0930` -> 09:30      four is HH:mm
 *
 * Returns null for anything that cannot be a time, which the caller treats as "leave what
 * was there" rather than as an error -- a half-typed `9:` is on its way somewhere, not wrong.
 */
export const parseTypedTime = (raw: string): string | null => {
    const digits: string = raw.replace(/\D/g, '');

    if (digits.length === 0 || digits.length > 4) {
        return null;
    }

    const [hours, minutes] = digits.length <= 2
        ? [Number(digits), 0]
        : [Number(digits.slice(0, digits.length - 2)), Number(digits.slice(-2))];

    if (hours > 23 || minutes > 59) {
        return null;
    }

    return `${pad(hours)}:${pad(minutes)}`;
};

/**
 * Whether an option should survive what has been typed so far.
 *
 * Matched on digits with the colon taken out of both sides, so `930` finds `09:30` -- a
 * plain substring match on the rendered text would not, and having the list go empty
 * halfway through typing a time you can see in it is the whole reason this exists.
 */
export const matchesTyped = (option: string, typed: string): boolean => {
    const typedDigits: string = typed.replace(/\D/g, '');

    return typedDigits.length === 0 || option.replace(':', '').includes(typedDigits);
};

/**
 * The option a value belongs next to, so an out-of-step time still opens the list in the
 * right place. A session recorded at 09:07 is a real row; the picker must not pretend it
 * is 09:00, but it should still scroll to where 09:07 would sit.
 */
export const nearestOption = (value: string, options: string[]): string | undefined => {
    const target: number | null = clockToMinutes(value);

    if (target === null || options.length === 0) {
        return undefined;
    }

    return options.reduce((best: string, option: string) => {
        const distance = (candidate: string): number =>
            Math.abs((clockToMinutes(candidate) ?? 0) - target);

        return distance(option) < distance(best) ? option : best;
    }, options[0]);
};
